/**
 * Browser errors reach the host, not just the console.
 *
 * `reportClientError` posts to the app's own Next route (/api/client-errors),
 * same origin, which symbolicates the stack against the build's source maps
 * on the server and forwards ONE JSON log line to the backend intake. From
 * there it is a normal structured log line (event=client_error) in Loki, with
 * the user's tenant and id, alertable like anything else. Nothing goes to a
 * third party.
 *
 * Rules: never throw from the reporter; never send more than the cap; dedupe
 * the same error for a minute so a render loop sends one report, not 400.
 */

const MAX_MESSAGE = 500;
const MAX_STACK = 8000;
const MAX_BREADCRUMBS = 20;
const DEDUPE_MS = 60_000;

const recent = new Map<string, number>();

function hash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/** Query strings can carry tokens; URLs in a report keep only the path. */
export function stripQuery(url: string): string {
  try { const u = new URL(url, typeof location !== 'undefined' ? location.origin : 'http://x'); return u.pathname; } catch { return url.split('?')[0]; }
}

export interface ClientErrorContext {
  route?: string;
  component?: string;
  breadcrumbs?: string[];
  /** Where it was caught: 'boundary' | 'error-page' | 'window' | 'unhandledrejection' | 'manual' */
  source?: string;
}

export interface ClientErrorReport {
  message: string;
  name: string;
  stack: string;
  route: string;
  component?: string;
  source: string;
  user_agent: string;
  breadcrumbs: string[];
  occurred_at: string;
}

export function buildReport(err: unknown, ctx: ClientErrorContext = {}): ClientErrorReport {
  const e = err instanceof Error ? err : new Error(typeof err === 'string' ? err : JSON.stringify(err ?? 'unknown'));
  return {
    message: (e.message || 'Error').slice(0, MAX_MESSAGE),
    name: e.name || 'Error',
    stack: (e.stack || '').slice(0, MAX_STACK),
    route: stripQuery(ctx.route ?? (typeof location !== 'undefined' ? location.pathname : '')),
    component: ctx.component,
    source: ctx.source ?? 'manual',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : '',
    breadcrumbs: (ctx.breadcrumbs ?? []).slice(-MAX_BREADCRUMBS).map((b) => String(b).slice(0, 200)),
    occurred_at: new Date().toISOString(),
  };
}

let csrfGetter: () => string | null = () => null;
/** The auth store registers how to read the CSRF token; this module never imports the store. */
export function setCsrfTokenGetter(fn: () => string | null): void { csrfGetter = fn; }

export async function reportClientError(err: unknown, ctx: ClientErrorContext = {}): Promise<void> {
  try {
    if (typeof window === 'undefined') return;
    const report = buildReport(err, ctx);
    const key = hash(`${report.name}|${report.message}|${report.stack.slice(0, 400)}`);
    const now = Date.now();
    const last = recent.get(key);
    if (last && now - last < DEDUPE_MS) return;
    recent.set(key, now);
    if (recent.size > 200) recent.clear();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const csrf = csrfGetter();
    if (csrf) headers['X-CSRF-Token'] = csrf;
    await fetch('/api/client-errors', {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: JSON.stringify(report),
      keepalive: true,
    });
  } catch {
    /* the reporter never throws */
  }
}

let installed = false;
/** Once per page: uncaught errors and unhandled promise rejections. */
export function installGlobalErrorReporting(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (ev) => {
    void reportClientError(ev.error ?? ev.message, { source: 'window' });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    void reportClientError(ev.reason, { source: 'unhandledrejection' });
  });
}
